/**
 * Gebruikersbeheer - Lijst van gebruikers
 * Alleen toegankelijk voor Admin
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersService, User, Role } from '../../services/users.service';
import { authService } from '../../services/auth.service';
import ConfirmDialog from '../../components/ConfirmDialog';
import ErrorDialog from '../../components/ErrorDialog';
import './Settings.css';

const UsersList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<number | undefined>();
  const [confirmToggle, setConfirmToggle] = useState<{ id: number; username: string; currentStatus: boolean } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; username: string } | null>(null);

  // Sticky header state
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [tableLeft, setTableLeft] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  // Sticky header scroll listener
  useEffect(() => {
    if (!users.length) return; // Wait for users to load

    const handleScroll = () => {
      if (tableHeaderRef.current) {
        const rect = tableHeaderRef.current.getBoundingClientRect();
        const shouldBeSticky = rect.bottom <= 185; // Show sticky when original scrolls to position 185

        // Always update position when scrolling
        setTableLeft(rect.left);
        setTableWidth(rect.width);
        setIsHeaderSticky(shouldBeSticky);
      }
    };

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      handleScroll(); // Check initial position
    }, 100);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [users]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersData, rolesData, currentUser] = await Promise.all([
        usersService.getAll({ limit: 100 }),
        usersService.getRoles(),
        authService.getCurrentUser()
      ]);

      setUsers(usersData.items);
      setRoles(rolesData);
      setCurrentUserId(currentUser.id);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het ophalen van gebruikers');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActiveClick = (id: number, username: string, currentStatus: boolean) => {
    // Prevent deactivating yourself
    if (id === currentUserId && currentStatus) {
      setError('Je kunt jezelf niet deactiveren');
      return;
    }

    setConfirmToggle({ id, username, currentStatus });
  };

  const handleToggleActiveConfirm = async () => {
    if (!confirmToggle) return;

    const action = confirmToggle.currentStatus ? 'deactiveren' : 'activeren';

    try {
      setError(null);
      if (confirmToggle.currentStatus) {
        // Deactiveren via delete endpoint
        await usersService.delete(confirmToggle.id);
      } else {
        // Activeren via update endpoint
        await usersService.update(confirmToggle.id, { is_active: true });
      }
      setConfirmToggle(null);
      loadData();
    } catch (err: any) {
      setError(err.message || `Er is een fout opgetreden bij het ${action}`);
      setConfirmToggle(null);
    }
  };

  const handleToggleActiveCancel = () => {
    setConfirmToggle(null);
  };

  const handlePermanentDeleteClick = (id: number, username: string) => {
    // Prevent deleting yourself
    if (id === currentUserId) {
      setError('Je kunt jezelf niet verwijderen');
      return;
    }

    setConfirmDelete({ id, username });
  };

  const handlePermanentDeleteConfirm = async () => {
    if (!confirmDelete) return;

    try {
      setError(null);
      await usersService.permanentDelete(confirmDelete.id);
      setConfirmDelete(null);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het verwijderen');
      setConfirmDelete(null);
    }
  };

  const handlePermanentDeleteCancel = () => {
    setConfirmDelete(null);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = !filterRole || user.rol_id === filterRole;

    return matchesSearch && matchesRole;
  });

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Gebruikersbeheer</h1>
          <p className="subtitle">Beheer gebruikers en hun rollen</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => navigate('/settings/users/new')}
        >
          + Nieuwe Gebruiker
        </button>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Zoek op gebruikersnaam of email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />

        <select
          value={filterRole || ''}
          onChange={(e) => setFilterRole(e.target.value ? parseInt(e.target.value) : undefined)}
          className="filter-select"
        >
          <option value="">Alle rollen</option>
          {roles.map(role => (
            <option key={role.id} value={role.id}>
              {role.naam}
            </option>
          ))}
        </select>
      </div>

      {/* Vaste header rij (altijd zichtbaar) */}
      {filteredUsers.length > 0 && (
        <div ref={tableHeaderRef} style={{
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          padding: '0.75rem 1rem',
          marginTop: '1rem',
          borderRadius: '8px 8px 0 0',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Gebruikersnaam</div>
          <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Email</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Rol</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Status</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Superuser</div>
          <div style={{ flex: '2', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Sticky header overlay (duplicaat) */}
      {isHeaderSticky && (
        <div style={{
          position: 'fixed',
          top: '185px',
          left: `${tableLeft}px`,
          width: `${tableWidth}px`,
          zIndex: 1000,
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          borderRadius: '8px 8px 0 0',
          padding: '0.75rem 1rem',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Gebruikersnaam</div>
          <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Email</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Rol</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Status</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Superuser</div>
          <div style={{ flex: '2', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Gebruikers lijst */}
      {filteredUsers.length === 0 ? (
        <div style={{
          padding: '3rem',
          backgroundColor: '#f1f5f9',
          borderRadius: '8px',
          textAlign: 'center',
          marginTop: '1rem'
        }}>
          Geen gebruikers gevonden
        </div>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '0 0 8px 8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          {filteredUsers.map((user) => (
            <div key={user.id} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.75rem 1rem',
              borderBottom: '1px solid #dee2e6',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <div style={{ flex: '1.2', minWidth: 0, fontWeight: 600, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.username}
              </div>
              <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.email}
              </div>
              <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.rol ? (
                  <span className="role-badge">
                    {user.rol.naam}
                  </span>
                ) : (
                  <span className="text-muted">Geen rol</span>
                )}
              </div>
              <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem', display: 'flex', alignItems: 'center' }}>
                <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                  {user.is_active ? 'Actief' : 'Inactief'}
                </span>
              </div>
              <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.is_superuser && (
                  <span className="superuser-badge">★ Superuser</span>
                )}
              </div>
              <div className="action-buttons" style={{ flex: '2', minWidth: 0 }}>
                <button
                  className="btn-small btn-secondary"
                  onClick={() => navigate(`/settings/users/${user.id}`)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Bewerken
                </button>
                <button
                  className={`btn-small ${user.is_active ? 'btn-warning' : 'btn-success'}`}
                  onClick={() => handleToggleActiveClick(user.id, user.username, user.is_active)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {user.is_active ? 'Deactiveren' : 'Activeren'}
                </button>
                <button
                  className="btn-small btn-danger"
                  onClick={() => handlePermanentDeleteClick(user.id, user.username)}
                  title="Permanent verwijderen (onomkeerbaar!)"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Verwijderen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="table-info">
        Totaal: {filteredUsers.length} gebruiker(s)
      </div>

      <ConfirmDialog
        isOpen={!!confirmToggle}
        title={confirmToggle?.currentStatus ? 'Gebruiker Deactiveren' : 'Gebruiker Activeren'}
        message={confirmToggle?.currentStatus
          ? `Weet je zeker dat je gebruiker "${confirmToggle?.username}" wilt deactiveren? De gebruiker kan niet meer inloggen.`
          : `Weet je zeker dat je gebruiker "${confirmToggle?.username}" wilt activeren? De gebruiker kan weer inloggen.`
        }
        confirmText={confirmToggle?.currentStatus ? 'Deactiveren' : 'Activeren'}
        cancelText="Annuleren"
        onConfirm={handleToggleActiveConfirm}
        onCancel={handleToggleActiveCancel}
        danger={confirmToggle?.currentStatus}
      />

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Gebruiker Permanent Verwijderen"
        message={`Weet je zeker dat je gebruiker "${confirmDelete?.username}" permanent wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt!`}
        confirmText="Permanent Verwijderen"
        cancelText="Annuleren"
        onConfirm={handlePermanentDeleteConfirm}
        onCancel={handlePermanentDeleteCancel}
        danger={true}
      />

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default UsersList;